import React, { useMemo, useState } from "react";

import { faMagnifyingGlass, faPlus } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonStandard, Icon, Text } from "@performyard/py-components";

import ChecklistItem from "./ChecklistItem";
import ChecklistSection from "./ChecklistSection";

import type { ItemStatus } from "../types";

import {
	useAddMeetingAgendaItem,
	useEditMeetingAgendaItem,
	useMeetingAgendaItems,
	useSearchService,
} from "../../../../api/hooks";
import { useChecklistItems, useGoalActionModals } from "../hooks";

import { SERVICE_GOAL } from "../../../../api/services/defs/search-service";
import type { Goal } from "../../../../api/services/searchServiceResultTypes/goal";

import { getParticipantName } from "../utils";

import { config } from "../config";
import { useMeetingContext } from "../context";
import { AddGoalsToAgendaModal } from "../modals";

/**
 * Agenda items section. Renders an accordion of agenda items in the right-side
 * panel and supports add / edit / delete plus pulling in goals as agenda items.
 */
const AgendaItemsSection = (): React.ReactElement => {
	const { meeting, isInteractionDisabled, userId, isManagerDirectReportMeeting, workspace, participantNames } =
		useMeetingContext();
	const { data: items, isLoading } = useMeetingAgendaItems(meeting._id);
	const { mutate: addItem } = useAddMeetingAgendaItem();
	const { mutate: editItem } = useEditMeetingAgendaItem();
	const [isAddGoalsOpen, setIsAddGoalsOpen] = useState(false);

	const {
		activeItems,
		isAddingItem,
		editingItemId,
		isAddButtonDisabled,
		handleAddButtonClick,
		handleEditStart,
		handleEditSave,
		handleNewItemSave,
		handleStatusChange,
	} = useChecklistItems({
		meetingId: meeting._id,
		currentUserId: userId,
		itemType: "agenda",
		items,
		isLoading,
		addItemMutation: addItem,
		editItemMutation: editItem,
		isInteractionDisabled,
	});

	const agendaGoalIds = useMemo(() => (items || []).map((item) => item.goalId).filter(Boolean) as string[], [items]);
	const goalsQuery = useSearchService<Goal>({
		service: SERVICE_GOAL,
		filters: { _id_in: agendaGoalIds },
		limit: agendaGoalIds.length || config.goals.listLimit,
		defaultErrorMessage: "There was an error getting goals",
		disabled: agendaGoalIds.length === 0,
	});
	const goalById = useMemo(
		() => new Map((goalsQuery.data?.results || []).map((goal) => [goal._id, goal])),
		[goalsQuery.data?.results]
	);
	const { handleActionSelect: handleGoalActionSelect, modals } = useGoalActionModals({
		onGoalUpdated: () => goalsQuery.refetch(),
	});

	// Per Figma, both add buttons hide while the inline create form is open —
	// the form itself is the affordance for the in-progress add.
	const addButton =
		!isInteractionDisabled && !isAddingItem ? (
			<div className="flex flex-wrap gap-2">
				<ButtonStandard
					iconLeft={faPlus}
					onClick={handleAddButtonClick}
					disabled={isAddButtonDisabled}
					dangerouslySetClassName={
						isAddButtonDisabled
							? "disabled:cursor-not-allowed disabled:pointer-events-auto disabled:hover:!bg-primary-5 w-fit flex-shrink-0"
							: "w-fit flex-shrink-0"
					}
				>
					Add Agenda Item
				</ButtonStandard>
				{isManagerDirectReportMeeting ? (
					<ButtonStandard
						iconLeft={faPlus}
						onClick={() => setIsAddGoalsOpen(true)}
						disabled={isInteractionDisabled}
						dangerouslySetClassName="w-fit flex-shrink-0"
					>
						Add Goal
					</ButtonStandard>
				) : null}
			</div>
		) : undefined;

	const hasItems = activeItems.length > 0;

	return (
		<>
			<ChecklistSection
				title="Agenda"
				isLoading={isLoading}
				isCollapsible={true}
				errorMessage="Failed to load agenda items"
				variant="sidebar"
				footer={addButton}
			>
				{hasItems
					? activeItems.map((item, index) => {
							const meetingItem = item.meetings.find((m) => m.meetingId === meeting._id);
							const goalId = item.goalId ?? undefined;

							return (
								<React.Fragment key={item._id}>
									{index > 0 && <div className="h-px w-full bg-neutral-2" />}
									<ChecklistItem
										id={item._id}
										name={item.name}
										status={(meetingItem?.status as ItemStatus) || "incomplete"}
										creatorId={item.created?.userId || ""}
										creatorName={getParticipantName(
											workspace,
											participantNames,
											item.created?.userId
										)}
										userId={userId}
										isEditing={editingItemId === item._id}
										itemType="agenda"
										onEdit={handleEditStart}
										onStatusChange={(status) => handleStatusChange(item._id, status)}
										onSave={handleEditSave}
										{...(goalId
											? {
													goalId,
													goal: goalById.get(goalId),
													onGoalActionSelect: handleGoalActionSelect,
												}
											: {})}
									/>
								</React.Fragment>
							);
						})
					: !isAddingItem && (
							<div className="flex flex-col items-center justify-center gap-[9px] px-4 py-1 text-center">
								<div className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-1">
									<Icon icon={faMagnifyingGlass} size={config.iconSize.large} color="neutral-7" />
								</div>
								<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
									No Agenda Items
								</Text>
							</div>
						)}

				{isAddingItem && (
					<ChecklistItem
						id="new-agenda-item"
						name=""
						status="incomplete"
						creatorId={userId}
						userId={userId}
						isEditing={true}
						isNewItem={true}
						itemType="agenda"
						onEdit={() => {}}
						onStatusChange={() => {}}
						onSave={handleNewItemSave}
					/>
				)}
			</ChecklistSection>
			<AddGoalsToAgendaModal
				isOpen={isAddGoalsOpen}
				onClose={() => setIsAddGoalsOpen(false)}
				existingGoalIds={agendaGoalIds}
			/>
			{modals}
		</>
	);
};

export default AgendaItemsSection;
